"use client";

import { Menu, ShieldEllipsis } from "lucide-react";
import NextLink from "next/link";
import { useSession } from "next-auth/react";
import { Link } from "next-view-transitions";
import Logo from "~/components/logo";
import { Button } from "~/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "~/components/ui/sheet";
import { usePathname } from "~/hooks/use-pathname";
import { cn } from "~/lib/utils";
import { paths } from "~/routes/paths";
import { navGroupIcons } from "./nav-icons";
import type { ResolvedNavGroup, ResolvedNavSection } from "./nav-resolver";

type Props = {
  dict: Dictionary;
  lang: AppLocale;
  groups: ResolvedNavGroup[];
  adminSection: ResolvedNavSection;
};

export function MobileNav({ dict, lang, groups, adminSection }: Props) {
  const { data: session } = useSession();
  const pathname = usePathname();
  const isActive = (href: string) => pathname === href;

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="shrink-0 rounded-xl lg:hidden"
        >
          <Menu className="h-5 w-5" />
          <span className="sr-only">{dict.Meny}</span>
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        aria-describedby={undefined}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="flex h-full w-[86vw] min-w-[320px] max-w-[420px] flex-col gap-0 p-0"
      >
        <SheetTitle className="sr-only">{dict.Meny}</SheetTitle>

        <div className="shrink-0 border-b py-4 pl-4 pr-14">
          <SheetClose asChild>
            <Link
              prefetch
              href={paths(lang).home}
              className="flex min-w-0 items-center gap-3"
            >
              <Logo className="h-9 w-9 shrink-0" disabledLink />
              <span className="flex min-w-0 flex-col leading-tight">
                <span className="truncate text-sm font-bold uppercase tracking-tight">
                  {dict["Stockholm Stråkstudio"]}
                </span>
                <span className="truncate text-xs text-muted-foreground">
                  {dict["Fiol & Celloundervisning"]}
                </span>
              </span>
            </Link>
          </SheetClose>
        </div>

        <nav
          className="min-h-0 flex-1 overflow-y-auto py-2"
          aria-label="Primary"
        >
          {groups.map((group) => {
            const Icon = navGroupIcons[group.icon];
            const single =
              group.items.length === 1 ? group.items[0] : undefined;
            const header = (
              <>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                  <Icon className="h-4 w-4 text-primary" aria-hidden />
                </span>
                <span className="truncate text-sm font-semibold">
                  {group.label}
                </span>
              </>
            );
            if (single) {
              const active = isActive(single.href);
              return (
                <div key={group.key} className="px-3 py-2">
                  <SheetClose asChild>
                    <NextLink
                      href={single.href}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "flex items-center gap-3 rounded-lg px-1 py-1 transition-colors hover:bg-muted",
                        active && "bg-primary/5",
                      )}
                    >
                      {header}
                    </NextLink>
                  </SheetClose>
                </div>
              );
            }
            return (
              <div key={group.key} className="px-3 py-2">
                <div className="flex items-center gap-3 px-1 pb-1.5">
                  {header}
                </div>
                <ul className="ml-5 space-y-0.5 border-l pl-3">
                  {group.items.map((item) => {
                    const active = isActive(item.href);
                    return (
                      <li key={item.labelKey}>
                        <SheetClose asChild>
                          <NextLink
                            href={item.href}
                            aria-current={active ? "page" : undefined}
                            className={cn(
                              "flex items-center rounded-lg px-3 py-2 text-sm text-foreground/75 transition-colors hover:bg-muted hover:text-foreground",
                              active &&
                                "bg-primary/10 font-medium text-primary",
                            )}
                          >
                            {item.label}
                          </NextLink>
                        </SheetClose>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}

          {session?.user.role === "ADMIN" && (
            <div className="border-t px-3 py-2">
              <div className="flex items-center gap-3 px-1 pb-1.5 pt-1">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
                  <ShieldEllipsis
                    className="h-4 w-4 text-muted-foreground"
                    aria-hidden
                  />
                </span>
                <span className="text-sm font-semibold">
                  {adminSection.label}
                </span>
              </div>
              <ul className="ml-5 space-y-0.5 border-l pl-3">
                {adminSection.children.map((child) => {
                  const active = isActive(child.href);
                  return (
                    <li key={child.labelKey}>
                      <SheetClose asChild>
                        <NextLink
                          href={child.href}
                          prefetch={
                            child.labelKey === "Logga ut" ? false : undefined
                          }
                          aria-current={active ? "page" : undefined}
                          className={cn(
                            "flex items-center rounded-lg px-3 py-2 text-sm text-foreground/75 transition-colors hover:bg-muted hover:text-foreground",
                            active && "bg-primary/10 font-medium text-primary",
                            child.variant === "ghost" &&
                              "font-medium text-destructive",
                          )}
                        >
                          {child.label}
                        </NextLink>
                      </SheetClose>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </nav>

        <div className="shrink-0 border-t p-4">
          <SheetClose asChild>
            <Button asChild className="h-11 w-full rounded-full">
              <Link prefetch href={paths(lang).contact}>
                {dict["Kontakta oss"]}
              </Link>
            </Button>
          </SheetClose>
        </div>
      </SheetContent>
    </Sheet>
  );
}
